"use client"

import { createContext, useContext, useEffect, useMemo, useRef, useState, type ReactNode } from "react"
import { toast } from "sonner"
import type { SocialProfile } from "@/lib/social/types"
import { useRouter } from "next/navigation"

import { ApiRequestError as SocialApiError } from '@/lib/client-http'
import { createClientResourceCache, type ClientResourceCache } from '@/lib/client-resource-cache'
export { ApiRequestError as SocialApiError } from '@/lib/client-http'

export async function socialApi<T>(path: string, options: { method?: string; body?: unknown; signal?: AbortSignal; keepalive?: boolean } = {}): Promise<T> {
  const multipart = options.body instanceof FormData
  const method = options.method ?? "GET"
  // A cold preview worker may briefly return a gateway error. Retry only the
  // read-only profile bootstrap; never replay profile creation or other writes.
  for (let attempt = 0; ; attempt++) {
    const response = await fetch(`/api/social${path}`, {
      method,
      credentials: "same-origin",
      cache: "no-store",
      signal: options.signal,
      keepalive: options.keepalive,
      headers: options.body === undefined || multipart ? undefined : { "Content-Type": "application/json" },
      body: options.body === undefined ? undefined : multipart ? options.body as FormData : JSON.stringify(options.body),
    })
    const data = await response.json().catch(() => null)
    if (path === '/me' && method === 'GET' && [502, 503, 504].includes(response.status) && attempt < 2) {
      await waitForProfileRetry(750 * (attempt + 1), options.signal)
      continue
    }
    if (!response.ok) {
      throw new SocialApiError(typeof data?.error === "string" ? data.error : response.status === 401 ? "Please log in to continue." : [502, 503, 504].includes(response.status) ? "The server is temporarily unavailable. Please retry in a moment." : "Something went wrong. Please try again.", response.status)
    }
    if (data === null) throw new Error("The server returned an unreadable response. Please try again.")
    if (typeof window !== 'undefined' && (method === 'POST' && path === '/stories' || ['PATCH', 'DELETE', 'PUT'].includes(method) && /^\/stories\/(settings|[\w.-]+)$/.test(path))) window.dispatchEvent(new Event('social:stories-changed'))
    if (method !== 'GET' && typeof window !== 'undefined' && (path.startsWith('/blocks/') || path.startsWith('/mutes/') || path.startsWith('/reports/') || method === 'DELETE' && path.startsWith('/posts/') || method === 'PATCH' && (path === '/me' || path.startsWith('/posts/')))) window.dispatchEvent(new Event('social:access-changed'))
    return data as T
  }
}

function waitForProfileRetry(delay: number, signal?: AbortSignal) {
  return new Promise<void>((resolve, reject) => {
    const aborted = () => { clearTimeout(timer); signal?.removeEventListener('abort', aborted); reject(signal?.reason ?? new DOMException('Aborted', 'AbortError')) }
    const timer = setTimeout(() => { signal?.removeEventListener('abort', aborted); resolve() }, delay)
    if (signal?.aborted) aborted()
    else signal?.addEventListener('abort', aborted, { once: true })
  })
}

export function errorMessage(error: unknown) {
  return error instanceof Error ? error.message : "Something went wrong. Please try again."
}

export function useSocialResource<T>(url: string | null, identityKey = 'default', { keepPreviousData = false }: { keepPreviousData?: boolean } = {}) {
  const [revision, setRevision] = useState(0)
  const key = `${identityKey}:${url}:${revision}`
  const [result, setResult] = useState<{ key: string; identityKey: string; data?: T; error?: string } | null>(null)

  useEffect(() => {
    if (url === null) return
    const controller = new AbortController()
    socialApi<T>(url, { signal: controller.signal }).then(
      data => { if (!controller.signal.aborted) setResult({ key, identityKey, data }) },
      error => { if (!controller.signal.aborted) setResult(previous => ({ key, identityKey, error: errorMessage(error), data: keepPreviousData && previous?.identityKey === identityKey ? previous.data : undefined })) },
    )
    return () => controller.abort()
  }, [url, key, identityKey, keepPreviousData])

  return {
    // Retention is opt-in and scoped to the current viewer. Consumers must use
    // `loading` to avoid showing a previous tab's content under a new label.
    data: result?.key === key || keepPreviousData && result?.identityKey === identityKey ? result?.data : undefined,
    error: result?.key === key ? result.error : undefined,
    loading: url !== null && result?.key !== key,
    reload: () => setRevision(value => value + 1),
    update: (data: T) => setResult(current => keepPreviousData && current?.key !== key ? current : { key, identityKey, data }),
  }
}

type Viewer = { profile: SocialProfile | null; user?: { id: string; name: string } | null }
type SocialContextValue = {
  profile: SocialProfile | null
  loading: boolean
  error?: string
  reload: () => void
  updateProfile: (profile: SocialProfile) => void
  requireProfile: () => boolean
  resourceCache?: ClientResourceCache<unknown>
}
const SocialContext = createContext<SocialContextValue | null>(null)

export function SocialProvider({ children }: { children: ReactNode }) {
  const existing = useContext(SocialContext)
  return existing ? children : <SocialProviderRoot>{children}</SocialProviderRoot>
}

function SocialProviderRoot({ children }: { children: ReactNode }) {
  const router = useRouter()
  const [revision, setRevision] = useState(0)
  const [result, setResult] = useState<{ revision: number; profile: SocialProfile | null; error?: string } | null>(null)
  const checkedAt = useRef(0)
  const identity = result?.profile?.id || 'anonymous'
  const resourceCache = useMemo(() => {
    async function fetchForViewer<T>(url: string, options: { signal?: AbortSignal }): Promise<T> {
      const data = await socialApi<T>(url, options)
      if (data && typeof data === 'object' && 'viewerId' in data && (data.viewerId || 'anonymous') !== identity) {
        window.dispatchEvent(new Event('social:auth-changed'))
        throw new SocialApiError('Your account changed. Reconnecting.', 401)
      }
      return data
    }
    return createClientResourceCache<unknown>(identity, 120000, undefined, fetchForViewer)
  }, [identity])
  useEffect(() => () => resourceCache.dispose(), [resourceCache])

  useEffect(() => {
    let active = true
    const controller = new AbortController()
    async function load() {
      try {
        let viewer = await socialApi<Viewer>("/me", { signal: controller.signal })
        // Existing platform accounts get a social profile before any personalized feed loads.
        if (!viewer.profile && viewer.user) viewer = await socialApi<Viewer>("/me", { method: "POST", body: {}, signal: controller.signal })
        if (active) { checkedAt.current = Date.now(); setResult({ revision, profile: viewer.profile }) }
      } catch (error) {
        if (active) setResult(previous => ({ revision, profile: error instanceof SocialApiError && [401, 403].includes(error.status) ? null : previous?.profile || null, error: errorMessage(error) }))
      }
    }
    void load()
    return () => { active = false; controller.abort() }
  }, [revision])

  useEffect(() => {
    const accessChanged = () => resourceCache.invalidate()
    const focus = () => { if (!document.hidden && Date.now() - checkedAt.current > 60000) { checkedAt.current = Date.now(); setRevision(value => value + 1) } }
    const authChanged = () => { resourceCache.invalidate(); setResult(null); setRevision(value => value + 1) }
    window.addEventListener('social:access-changed', accessChanged); window.addEventListener('focus', focus); document.addEventListener('visibilitychange', focus); window.addEventListener('social:auth-changed', authChanged)
    return () => { window.removeEventListener('social:access-changed', accessChanged); window.removeEventListener('focus', focus); document.removeEventListener('visibilitychange', focus); window.removeEventListener('social:auth-changed', authChanged) }
  }, [resourceCache])

  const loading = result === null
  const profile = !loading ? result?.profile ?? null : null
  const error = !loading ? result?.error : undefined
  return (
    <SocialContext.Provider value={{
      profile, loading, error,
      resourceCache,
      reload: () => setRevision(value => value + 1),
      updateProfile: profile => setResult({ revision, profile }),
      requireProfile: () => {
        if (profile) return true
        if (loading) toast.info("Your profile is still loading. Please try again in a moment.")
        else if (error) toast.error("Your profile could not be loaded. Use Retry above to reconnect.")
        else toast.info("Log in to join the conversation.", { action: { label: "Log in", onClick: () => router.push('/login') } })
        return false
      },
    }}>{children}</SocialContext.Provider>
  )
}

export function useSocial() {
  const context = useContext(SocialContext)
  if (!context) throw new Error("Social components must be inside SocialProvider")
  return context
}
